import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import { Button } from '@components/ui'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential: string }) => void
          }) => void
          renderButton: (
            element: HTMLElement,
            config: { theme: string; size: string; width: number }
          ) => void
        }
      }
    }
  }
}

const GOOGLE_CLIENT_ID = '759138193168-4p3hjiuv841k1qnsialtf1o5suqoeian.apps.googleusercontent.com'

function parseJwt(token: string) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  )
  return JSON.parse(jsonPayload)
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const redirectTo = (location.state as { from?: Location })?.from?.pathname || searchParams.get('redirect') || '/home'
  const { isAuthenticated, setUser, setIdToken, setDevMode, setLoading } = useAuthStore()

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true })
      return
    }

    // Initialize Google Sign-In
    const initializeGoogle = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
        })

        const buttonDiv = document.getElementById('googleSignInButton')
        if (buttonDiv) {
          window.google.accounts.id.renderButton(buttonDiv, {
            theme: 'filled_black',
            size: 'large',
            width: 280,
          })
        }
      }
    }

    // Wait for Google script to load
    if (window.google) {
      initializeGoogle()
    } else {
      const checkGoogle = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogle)
          initializeGoogle()
        }
      }, 100)

      return () => clearInterval(checkGoogle)
    }

    setLoading(false)
  }, [isAuthenticated, navigate, setLoading])

  const handleCredentialResponse = (response: { credential: string }) => {
    const decoded = parseJwt(response.credential)
    setIdToken(response.credential) // Store raw JWT for API auth
    setUser({
      email: decoded.email,
      name: decoded.name,
      picture: decoded.picture,
      isAdmin: false, // Will be set by store
    })
    navigate(redirectTo, { replace: true })
  }

  const handleDevBypass = () => {
    setDevMode(true)
    navigate(redirectTo, { replace: true })
  }

  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="bg-[var(--bg-secondary)] rounded-lg p-8 border border-[var(--border-color)]">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center">
              <span className="text-[var(--text-primary)] font-bold text-2xl">F1</span>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-xl font-semibold text-center text-[var(--text-primary)] mb-2">
            DealHub
          </h1>
          <p className="text-sm text-[var(--text-muted)] text-center mb-8">
            Sign in with your FrankieOne account
          </p>

          {/* Google Sign-In Button */}
          <div className="flex justify-center mb-4">
            <div id="googleSignInButton"></div>
          </div>

          {/* Dev Mode Bypass */}
          {isLocalhost && (
            <div className="mt-6 pt-6 border-t border-[var(--border-color)]">
              <p className="text-xs text-[var(--text-muted)] text-center mb-3">
                Development Mode
              </p>
              <Button
                variant="ghost"
                className="w-full"
                onClick={handleDevBypass}
              >
                Skip Authentication
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="mt-4 text-xs text-gray-600 text-center">
          Only @frankieone.com accounts are allowed
        </p>
      </div>
    </div>
  )
}
